import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Plus, Trash, PencilSimple, Ruler } from '@phosphor-icons/react';

export default function EenhedenPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ value: '', label: '' });
  const [editId, setEditId] = useState(null);

  const load = () => {
    API.get('/eenheden').then(r => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.value.trim() || !form.label.trim()) {
      alert('Vul zowel code als label in.');
      return;
    }
    setBusy(true);
    try {
      if (editId) {
        await API.put(`/eenheden/${editId}`, form);
      } else {
        await API.post('/eenheden', form);
      }
      setForm({ value: '', label: '' });
      setEditId(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Opslaan mislukt');
    } finally { setBusy(false); }
  };

  const startEdit = (e) => {
    setForm({ value: e.value, label: e.label });
    setEditId(e.id);
  };

  const cancelEdit = () => {
    setForm({ value: '', label: '' });
    setEditId(null);
  };

  const remove = async (e) => {
    if (!window.confirm(`Eenheid "${e.label}" verwijderen?`)) return;
    try {
      await API.delete(`/eenheden/${e.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const builtin = items.filter(x => x.builtin);
  const custom = items.filter(x => !x.builtin);

  return (
    <div className="space-y-6" data-testid="eenheden-panel">
      <div>
        <h2 className="text-xl font-bold text-[#0A0A0A] tracking-tight inline-flex items-center gap-2">
          <Ruler size={22} /> Eenheden
        </h2>
        <p className="text-sm text-gray-500 mt-1">Beheer welke eenheden beschikbaar zijn in de artikel-dropdown. Ingebouwde eenheden zijn vast; custom eenheden kan je toevoegen of aanpassen.</p>
      </div>

      {/* Ingebouwd */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Ingebouwde eenheden ({builtin.length})</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {builtin.map(e => (
            <div key={e.value} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 text-xs">
              <span className="font-mono font-bold text-gray-900 shrink-0 w-16">{e.value}</span>
              <span className="text-gray-700 flex-1 truncate">{e.label}</span>
              <span className="text-[9px] uppercase text-gray-400 bg-white px-1.5 py-0.5 border border-gray-200">Standaard</span>
            </div>
          ))}
        </div>
      </div>

      {/* Custom */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Eigen eenheden ({custom.length})</div>
        {custom.length === 0 ? (
          <div className="px-3 py-4 bg-gray-50 border border-dashed border-gray-300 text-xs text-gray-500 text-center">
            Nog geen eigen eenheden. Voeg hieronder uw eerste toe.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {custom.map(e => (
              <div key={e.id} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-xs hover:border-[#002FA7]/40">
                <span className="font-mono font-bold text-gray-900 shrink-0 w-16">{e.value}</span>
                <span className="text-gray-700 flex-1 truncate">{e.label}</span>
                <button className="text-[#002FA7] hover:bg-blue-50 px-1" onClick={() => startEdit(e)} title="Bewerken" data-testid={`eenheid-edit-${e.id}`}>
                  <PencilSimple size={12} />
                </button>
                <button className="text-red-500 hover:bg-red-50 px-1" onClick={() => remove(e)} title="Verwijderen" data-testid={`eenheid-del-${e.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulier voor nieuw/bewerk */}
      <div className="p-4 bg-[#F9FAFB] border border-gray-200">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          {editId ? `Eenheid bewerken` : 'Nieuwe eenheid toevoegen'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Code</label>
            <input
              className="field-input font-mono"
              placeholder="bv. dag, set, rol"
              value={form.value}
              onChange={e => setForm(p => ({ ...p, value: e.target.value.toLowerCase() }))}
              maxLength={20}
              data-testid="eenheid-form-value"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-1">Label (op PDF)</label>
            <input
              className="field-input"
              placeholder="bv. Per dag, Per set, Per rol"
              value={form.label}
              onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
              maxLength={50}
              data-testid="eenheid-form-label"
            />
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
              onClick={save}
              disabled={busy}
              data-testid="eenheid-form-save"
            >
              <Plus size={14} /> {editId ? 'Opslaan' : 'Toevoegen'}
            </button>
            {editId && (
              <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Annuleren</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
